import type { CSSObject } from '@emotion/react'

/**
 * Map CSS’s `font-weight` names to the corresponding numbers.
 *
 * @note Only use this if you need to customize the `font-weight` provided by
 *   `Typography`.
 */
export const FontWeight = {
  Medium: 500,
  Semibold: 600,
  Bold: 700,
} as const

export type FontWeights = (typeof FontWeight)[keyof typeof FontWeight]

/**
 * Standardized typefaces (`font-family`) used in the Gem Design System.
 *
 * @note Only use this if you need to customize the `font-family` provided by
 *   `Typography`.
 */
export const Typeface = {
  Default:
    'Proxima Nova, Open Sans, Gill Sans, Corbel, Arial, system-ui, sans-serif',
  Monospace: 'ui-monospace, Menlo, Consolas, monospace',
} as const

type TextSizes =
  | 'H25'
  | 'H50'
  | 'H100'
  | 'H300'
  | 'H400'
  | 'H500'
  | 'H700'
  | 'T100'
  | 'T700'
  | 'T300'
  | 'T500'
  | 'M500'

/** The label twin of each paragraph style: `HL50` labels `H50`, and so on. */
type LabelSizes =
  | 'HL25'
  | 'HL50'
  | 'HL100'
  | 'HL300'
  | 'HL400'
  | 'HL500'
  | 'HL700'
  | 'TL100'
  | 'TL300'
  | 'TL500'
  | 'TL700'
  | 'ML500'

export type StandardTypographyStyle = Pick<
  CSSObject,
  'fontSize' | 'lineHeight'
> & {
  fontFamily: (typeof Typeface)[keyof typeof Typeface]
  fontWeight: FontWeights
}

export type StandardTypography = Record<TextSizes, StandardTypographyStyle> &
  Record<LabelSizes, StandardTypographyStyle & { textBox: string }>
